import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import axios from "axios";
import { getAuthHttpHeaders } from "../auth";
import { Button } from "semantic-ui-react";
import { instantFromApi, localDateTimeFromApi } from "../utils/dateTime";
import { formatCrypto, formatFiat, formatPercent, formatPercent2 } from "../utils/numberFormat";
import { ChronoUnit, LocalDateTime } from "js-joda";
import { VictoryLine, VictoryChart, VictoryTheme } from "victory";
import Dimensions from "react-dimensions";

const Periods = {
  "3D": 3,
  "1W": 7,
  "1M": 31,
  "3M": 3 * 31,
  "6M": 6 * 31,
  "1Y": 365,
  ALL: 365 * 100,
};

@inject("store")
@observer
class ExchangeAccountBalance extends Component {
  state = {
    simGroup: null,
    y0: false,
    chartPeriod: "1W",
  };

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  load = async () => {
    const {
      match: {
        params: { exchangeAccountId },
      },
    } = this.props;
    this.setState({ loading: true });

    try {
      let axiosRequest = axios.create({
        baseURL: "api",
        headers: {
          Accept: "application/json",
          ...getAuthHttpHeaders(),
        },
      });

      let response = await axiosRequest.get("exchangeAccount/balanceHistory/" + exchangeAccountId);

      this.setState({ balanceHistory: response.data });
    } finally {
      this.setState({ loading: false });
    }
  };

  Chart = ({ field }) => {
    const { balanceHistory, y0, chartPeriod } = this.state;
    const {
      containerWidth,
      store: { mobile },
    } = this.props;
    const width = containerWidth / (mobile ? 1 : 2) - 10;
    const height = width / 2;

    const data = balanceHistory.filter(balance =>
      localDateTimeFromApi(balance.at).isAfter(LocalDateTime.now().minusDays(Periods[chartPeriod]))
    );

    if (!data.length) {
      return undefined;
    }

    const current = data[0][field];
    const init = data[data.length - 1][field];
    const min = data.map(balance => balance[field]).reduce((a, b) => Math.min(a, b));
    const max = data.map(balance => balance[field]).reduce((a, b) => Math.max(a, b));

    const runDays =
      data.length &&
      instantFromApi(data[data.length - 1]["at"]).until(instantFromApi(data[0]["at"]), ChronoUnit.SECONDS) /
        (60 * 60 * 24);
    const profit = current - init;
    const profitRelative = runDays ? Math.pow(1 + profit / init, 1 / runDays) - 1 : 0;

    const format = field === "btc" ? formatCrypto : formatFiat;

    return (
      <div style={{ display: "inline-block", width, height }}>
        <div style={{ textAlign: "center", margin: 0 }}>
          <span style={{ textTransform: "uppercase", fontWeight: "bold" }}>
            {field}: {format(current)}
          </span>
          , initial:&nbsp;{format(init)}&nbsp;(<b>
            {formatPercent(profit / current)}&nbsp;...&nbsp;{formatPercent2(profitRelative)}
            <sup>^1</sup>/<sub>{Math.round(runDays)}D</sub>
          </b>) , {mobile && <br />}min:&nbsp;{format(min)}&nbsp;({formatPercent((current - min) / current)}),
          max:&nbsp;{format(max)}&nbsp;({formatPercent((current - max) / current)})
        </div>
        <VictoryChart
          scale={{ x: "time", y: "linear" }}
          width={width}
          height={height}
          domainPadding={5}
          minDomain={{ y: y0 ? 0 : undefined }}
          theme={VictoryTheme.material}
        >
          <VictoryLine data={data} x={balance => instantFromApi(balance.at).toEpochMilli()} y={field} />
        </VictoryChart>
      </div>
    );
  };

  ChartPeriodButton = ({ period }) => (
    <Button
      content={period}
      toggle
      size="tiny"
      style={{ paddingLeft: 8, paddingRight: 8 }}
      active={this.state.chartPeriod === period}
      onClick={() =>
        this.setState({
          chartPeriod: period,
        })
      }
    />
  );

  render() {
    const {
      match: {
        params: { exchangeAccountId },
      },
    } = this.props;

    const { balanceHistory, y0, loading } = this.state;

    if (!balanceHistory) {
      return <div>Loading Balance History for Exchange Account ${exchangeAccountId}...</div>;
    }

    return (
      <div>
        <h1>Exchange Account {exchangeAccountId.substring(0, 4)} Balance History</h1>

        <div>
          <Button icon="refresh" size="tiny" onClick={this.load} loading={loading} />
          <Button
            content="Y0"
            toggle
            size="tiny"
            active={y0}
            onClick={() =>
              this.setState(({ y0 }) => ({
                y0: !y0,
              }))
            }
          />

          {Object.keys(Periods).map(period => <this.ChartPeriodButton key={period} period={period} />)}

          <div style={{ textAlign: "center" }}>
            <this.Chart field="btc" />
            <this.Chart field="eur" />
          </div>
        </div>
      </div>
    );
  }
}

export default Dimensions()(ExchangeAccountBalance);



// WEBPACK FOOTER //
// ./src/exchangeAccounts/ExchangeAccountBalance.js